/**
 * Recursive tree browser for the local CMS.
 *
 * No tree primitive exists in src/components/ui, so this is hand-rolled:
 * a self-recursing row list with per-node expand state. Depth is unbounded
 * because sections nest arbitrarily.
 */

import { useState, useCallback, useEffect, useMemo, useContext } from "react"
import { PendingDot, PendingIdsContext } from "../shared/components/PendingChanges"
import { FilterBar, matchesWorkflow, workflowFilterDefs, type FilterDef, type FilterValue, type RecordInfo } from "../shared/components/RecordFilters"
import { createPortal } from "react-dom"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { ScrollArea } from "@/components/ui/scroll-area"
import { cn } from "@/lib/utils"
import {
  ChevronRight, ChevronDown, FolderOpen, Folder, FileText, Plus, Trash2,
  Search, RefreshCw, MoreHorizontal,
} from "lucide-react"
import * as db from "./store"
import { type KBStore, nodeTitle, articleState } from "./types"
import { coverageOf, describeLanguage, translationStatus, STATUS_LABEL, type LanguageInfo } from "../shared/languages"
import { StatusDot } from "../shared/components/languages/StatusDot"
import { CoverageLine } from "../shared/components/languages/CoverageLine"

interface ArticleTreeBrowserProps {
  store: KBStore
  locale: string
  language?: LanguageInfo
  selectedId: string | null
  onSelect: (id: string) => void
  onCreateCategory: () => void
  onCreateSection: (categoryId: string, parentSectionId: string | null) => void
  onCreateArticle: (sectionId: string) => void
  onDelete: (id: string) => void
  // Unpublished changes, review dates and owners, for the filters
  recordInfo?: (id: string) => RecordInfo | undefined
  people?: { id: string; name: string }[]
}

type NodeKindLocal = "category" | "section" | "article"

/** Everything a Row needs from its browser, bundled so call sites stay readable. */
interface RowContext {
  expanded: Record<string, boolean>
  selectedId: string | null
  matches: Set<string> | null
  toggle: (id: string) => void
  onSelect: (id: string) => void
  setMenuFor: (v: { id: string; kind: NodeKindLocal; x: number; y: number } | null) => void
  titleOf: (id: string) => string
  // The node's state in the working language, and its label
  statusOf: (id: string) => { status: ReturnType<typeof translationStatus>; title: string } | null
}

/**
 * One node of the KB tree.
 *
 * Declared at module scope, NOT inside ArticleTreeBrowser: a component defined during
 * render gets a new identity every time its parent renders, so React remounts
 * the whole subtree instead of updating it. ArticleTreeBrowser holds its filter box in
 * local state, so that meant every keystroke tore down and rebuilt the entire
 * tree. The values it used to close over are explicit props now.
 */
function Row({
  id, kind, depth, children, hasChildren, badge, ctx,
}: {
  id: string; kind: NodeKindLocal; depth: number
  children?: React.ReactNode; hasChildren?: boolean; badge?: React.ReactNode
  ctx: RowContext
}) {
  const pendingIds = useContext(PendingIdsContext)
  const { expanded, selectedId, matches, toggle, onSelect, setMenuFor, titleOf, statusOf } = ctx
  const st = statusOf(id)
  const isOpen = expanded[id] ?? depth < 1
  const isSel = selectedId === id
  const dimmed = matches ? !matches.has(id) : false
  const Icon = kind === "article" ? FileText : isOpen ? FolderOpen : Folder

  return (
    <div>
      <div
        className={cn(
          "group flex items-center gap-1 rounded px-1 py-1 text-sm",
          isSel ? "bg-primary/10 text-primary" : "hover:bg-muted/60",
          dimmed && "opacity-40",
        )}
        style={{ paddingLeft: depth * 12 + 4 }}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); toggle(id) }}
            className="shrink-0 rounded p-0.5 hover:bg-muted"
            aria-label={isOpen ? "Collapse" : "Expand"}
          >
            {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          </button>
        ) : (
          <span className="w-4 shrink-0" />
        )}
        <button
          type="button"
          onClick={() => onSelect(id)}
          onContextMenu={(e) => { e.preventDefault(); setMenuFor({ id, kind, x: e.clientX, y: e.clientY }) }}
          className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
          title={titleOf(id)}
        >
          <Icon className="h-3.5 w-3.5 shrink-0 opacity-70" />
          <span className="truncate">{titleOf(id)}</span>
          {pendingIds.has(id) && <PendingDot />}
          {st && (st.status === "missing" || st.status === "unreviewed") && <StatusDot status={st.status} title={st.title} />}
          {badge}
        </button>
        {/*
          A single always-visible menu button rather than a hover-revealed row
          of icons. The old layout put the actions after a flex-1 title, so a
          long title pushed them outside the panel where they couldn't be
          clicked — and hover-only meant they were invisible until then too.
        */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setMenuFor({ id, kind, x: e.clientX, y: e.clientY }) }}
          className="shrink-0 rounded p-0.5 text-muted-foreground opacity-60 hover:bg-muted hover:text-foreground group-hover:opacity-100"
          title="Actions"
          aria-label="Actions"
        >
          <MoreHorizontal className="h-3.5 w-3.5" />
        </button>
      </div>
      {isOpen && children}
    </div>
  )
}

export function ArticleTreeBrowser({
  store, locale, language = describeLanguage(locale), selectedId, onSelect,
  onCreateCategory, onCreateSection, onCreateArticle, onDelete, recordInfo, people = [],
}: ArticleTreeBrowserProps) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [filters, setFilters] = useState<FilterValue>({})
  const pendingIdsFilter = useContext(PendingIdsContext)
  const [query, setQuery] = useState("")
  // Node action menu, positioned at the click so it can't be clipped by the panel.
  const [menuFor, setMenuFor] = useState<{ id: string; kind: "category" | "section" | "article"; x: number; y: number } | null>(null)

  // Dismiss on any outside click, scroll, or Escape.
  useEffect(() => {
    if (!menuFor) return
    const close = () => setMenuFor(null)
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") close() }
    window.addEventListener("click", close)
    window.addEventListener("scroll", close, true)
    window.addEventListener("keydown", onKey)
    return () => {
      window.removeEventListener("click", close)
      window.removeEventListener("scroll", close, true)
      window.removeEventListener("keydown", onKey)
    }
  }, [menuFor])

  const toggle = useCallback((id: string) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }))
  }, [])

  const t = (id: string) => {
    const n = db.getNode(store, id)
    return n ? nodeTitle(n, locale, store.defaultLocale) : "(missing)"
  }

  // Both scan the whole KB, and both ran on EVERY render — including ones that
  // have nothing to do with either, like opening the row action menu or
  // expanding a folder. Memoizing ties them to what they actually depend on.
  const matches = useMemo(
    () => query.trim()
      ? new Set(db.searchNodes(store, query, 200).map(r => r.node.id))
      : null,
    [store, query],
  )

  const stats = useMemo(() => db.stats(store), [store])
  const coverage = useMemo(() => coverageOf(
    [...Object.values(store.categories), ...Object.values(store.sections), ...Object.values(store.articles)],
    locale, store.defaultLocale,
  ), [store, locale])
  const statusOf = (id: string) => {
    const n = db.getNode(store, id)
    if (!n) return null
    const status = translationStatus(n.translations, locale, store.defaultLocale)
    return { status, title: `${language.name}: ${STATUS_LABEL[status]}` }
  }

  const renderSections = (categoryId: string, parentSectionId: string | null, depth: number): React.ReactNode =>
    db.childSections(store, { categoryId, parentSectionId }).map(sec => {
      const nested = db.childSections(store, { categoryId, parentSectionId: sec.id })
      const articles = db.articlesIn(store, sec.id)
      return (
        <Row
          ctx={rowCtx}
          key={sec.id} id={sec.id} kind="section" depth={depth}
          hasChildren={nested.length > 0 || articles.length > 0}
          badge={
            <span className="ml-auto flex items-center gap-1 pl-1">
              {articles.length > 0 && (
                <span className="text-[10px] text-muted-foreground">{articles.length}</span>
              )}
            </span>
          }
        >
          {renderSections(categoryId, sec.id, depth + 1)}
          {articles.map(a => (
            <Row
              ctx={rowCtx}
              key={a.id} id={a.id} kind="article" depth={depth + 1}
              badge={
                <span className="ml-auto flex items-center gap-1 pl-1">
                  {a.translations[locale] && articleState(a, locale) === "draft" && (
                    <Badge
                      variant="outline"
                      className="h-4 px-1 text-[9px] text-muted-foreground"
                      title={`${language.name} is a draft`}
                    >
                      draft
                    </Badge>
                  )}
                </span>
              }
            />
          ))}
        </Row>
      )
    })

  // Rebuilt per render, but Row itself is stable at module scope, so rows
  // update rather than remount.
  const rowCtx: RowContext = {
    expanded, selectedId, matches, toggle, onSelect, setMenuFor, titleOf: t, statusOf,
  }

  const categories = db.allCategories(store)

  const filterDefs: FilterDef[] = [
    { key: "state", label: "Status", options: [{ value: "published", label: "Published" }, { value: "draft", label: "Draft" }] },
    { key: "translation", label: language.name, options: [
      { value: "missing", label: "Missing" }, { value: "unreviewed", label: "Needs review" }, { value: "reviewed", label: "Reviewed" },
    ] },
    { key: "category", label: "Category", options: categories.map(c => ({ value: c.id, label: t(c.id) })) },
    ...workflowFilterDefs(people),
  ]
  // With filters on, the tree becomes a flat list of the articles that match
  const filtered = useMemo(() => {
    if (!Object.keys(filters).length) return null
    const f = filters
    return Object.values(store.articles).filter(a => {
      if (f.state && (!a.translations[locale] || articleState(a, locale) !== f.state)) return false
      if (f.translation && translationStatus(a.translations, locale, store.defaultLocale) !== f.translation) return false
      if (f.category && store.sections[a.sectionId]?.categoryId !== f.category) return false
      if (query.trim() && matches && !matches.has(a.id)) return false
      return matchesWorkflow(recordInfo?.(a.id), f)
    }).sort((a, b) => t(a.id).localeCompare(t(b.id)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, store, locale, query, matches, recordInfo])

  return (
    <div className="flex h-full flex-col gap-2">
      <div className="flex items-center gap-1 px-1">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter…"
            className="h-7 pl-7 text-xs"
          />
        </div>
        <Button size="icon" variant="ghost" className="h-7 w-7" title="New category" onClick={onCreateCategory}>
          <Plus className="h-3.5 w-3.5" />
        </Button>
      </div>

      <div className="-mx-0 [&>div]:border-b-0 [&>div]:px-0">
        <FilterBar section="articles" defs={filterDefs} value={filters} onChange={setFilters} />
      </div>

      <div className="flex flex-wrap gap-1 px-1 text-[10px] text-muted-foreground">
        <span>{stats.categories} cat</span>·
        <span>{stats.sections} sec</span>·
        <span>{stats.articles} art</span>·
        <span>{stats.drafts} draft</span>
      </div>

      <ScrollArea className="flex-1 min-h-0 [&_[data-slot=scroll-area-viewport]>:first-child]:block!">
        <div className="pr-2">
          {filtered && (filtered.length === 0
            ? <p className="p-3 text-xs text-muted-foreground">No articles match these filters.</p>
            : filtered.map(a => (
              <button
                key={a.id}
                type="button"
                onClick={() => onSelect(a.id)}
                className={cn(
                  "flex w-full flex-col items-start rounded px-2 py-1.5 text-left hover:bg-muted/60",
                  selectedId === a.id && "bg-primary/10",
                )}
              >
                <span className="flex w-full items-center gap-1.5 text-sm">
                  <FileText className="h-3.5 w-3.5 shrink-0 opacity-70" />
                  <span className="truncate">{t(a.id)}</span>
                  {pendingIdsFilter.has(a.id) && <PendingDot />}
                </span>
                <span className="block w-full truncate pl-5 text-[11px] text-muted-foreground">{db.pathOf(store, a.id, locale)}</span>
              </button>
            )))}
          {!filtered && categories.length === 0 && (
            <p className="p-3 text-xs text-muted-foreground">
              Empty knowledge base. Create a category, or import a folder from the Jobs tab.
            </p>
          )}
          {!filtered && categories.map(cat => {
            const secs = db.childSections(store, { categoryId: cat.id, parentSectionId: null })
            return (
              <Row
                ctx={rowCtx}
                key={cat.id} id={cat.id} kind="category" depth={0}
                hasChildren={secs.length > 0}
              >
                {renderSections(cat.id, null, 1)}
              </Row>
            )
          })}
        </div>
      </ScrollArea>

      <CoverageLine language={language} isSource={locale === store.defaultLocale} coverage={coverage} noun="items" />

      {/*
        Portalled to document.body so the menu is never clipped by the panel's
        overflow — the original inline buttons were unreachable for exactly that
        reason. Clamped to the viewport so it stays on screen near the edges.
      */}
      {menuFor && createPortal(
        <div
          className="fixed z-50 min-w-[180px] overflow-hidden rounded-md border bg-popover py-1 shadow-lg"
          style={{
            left: Math.min(menuFor.x, window.innerWidth - 200),
            top: Math.min(menuFor.y, window.innerHeight - 180),
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="truncate px-3 py-1 text-[10px] uppercase tracking-wide text-muted-foreground">
            {menuFor.kind}
          </div>
          {menuFor.kind === "category" && (
            <MenuItem
              icon={Plus} label="Add section"
              onClick={() => { onCreateSection(menuFor.id, null); setMenuFor(null) }}
            />
          )}
          {menuFor.kind === "section" && (
            <>
              <MenuItem
                icon={FileText} label="Add article"
                onClick={() => { onCreateArticle(menuFor.id); setMenuFor(null) }}
              />
              <MenuItem
                icon={Plus} label="Add nested section"
                onClick={() => {
                  const sec = store.sections[menuFor.id]
                  if (sec) onCreateSection(sec.categoryId, menuFor.id)
                  setMenuFor(null)
                }}
              />
            </>
          )}
          <MenuItem
            icon={Trash2} label="Delete" destructive
            onClick={() => { onDelete(menuFor.id); setMenuFor(null) }}
          />
        </div>,
        document.body,
      )}
    </div>
  )
}

function MenuItem({
  icon: Icon, label, onClick, destructive,
}: { icon: any; label: string; onClick: () => void; destructive?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-muted",
        destructive && "text-destructive",
      )}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" />
      {label}
    </button>
  )
}
